import React, { useMemo } from 'react'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import DoublePointTakeoverBackground from './DoublePointTakeoverBackground'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import TextLink from 'LuxKit/TextLink'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { useAppSelector } from 'hooks/reduxHooks'
import DoublePointTakeoverTopBenefits from './DoublePointTakeoverTopBenefits'
import TextButton from 'LuxKit/Button/TextButton'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import LoyaltyTierPill from 'loyalty/components/LoyaltyTierPill'
import useDoublePointsCampaignTier from '../hooks/useDoublePointsCampaignTier'
import { getThemeClassName } from 'lib/theme/themeUtils'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import config from 'constants/config'

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
`

const HeroContainer = styled(Pane)`
  position: relative;
  padding-top: ${rem(48)};
  padding-bottom: ${rem(48)};
`

const Content = styled(VerticalSpacer)`
  position: relative;
  z-index: 1;
`

const CTAButtonWrapper = styled.div`
  button, a {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};
    }
    
    &:active {
      background-color: ${props => props.theme.palette.neutral.default.six};
    }
  }
`

const TextContent = styled(VerticalSpacer)`
  max-width: ${rem(400)};

  ${mediaQueryUp.tablet} {
    max-width: none;
  }
`

const HeroContent = styled.div`
  display: grid;
  gap: ${rem(24)};
  justify-content: center;

  ${mediaQueryUp.tablet} {
    justify-content: stretch;
    gap: ${rem(40)};
    grid-template-columns: 1fr auto;
    align-items: center;
  }


  &.tier-bronze {
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

interface Props {
  className?: string
  onJoin?: () => void
}

const anywhereSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
})

function DoublePointTakeoverHeroSection(props: Props) {
  const { className, onJoin } = props

  const [tier, tierName, , nextTierName] = useCurrentLoyaltyTier()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const displayTier = useDoublePointsCampaignTier()

  const isPlatinumMember = hasLoyaltyAccount && tier === 'platinum'
  const isMember = hasLoyaltyAccount && tier !== 'platinum'

  const HeaderText = useMemo(() => {
    if (isPlatinumMember) {
      return <>Extend your <i>{tierName}</i> status</>
    }
    if (isMember) {
      return <>Fast-track to <i>{nextTierName ?? tierName}</i> status</>
    }
    return <>Fast-track your status</>
  }, [isPlatinumMember, isMember, tierName, nextTierName])

  const SubtitleText = useMemo(() => {
    if (isPlatinumMember) {
      return <>For a limited time, earn double status credits on all hotels, tours and cruises, helping you maintaining your VIP benefits.</>
    }
    if (isMember) {
      return <>For a limited time, earn double status credits on all hotels, tours and cruises, helping you unlock <i>{nextTierName}</i> benefits faster.</>
    }
    return <>For a limited time, earn double status credits on all hotels, tours and cruises, helping you unlock benefits faster.</>
  }, [isPlatinumMember, isMember, nextTierName])

  return (
    <HeroContainer className={className}>
      <SiteTakeoverBackground>
        <DoublePointTakeoverBackground />
      </SiteTakeoverBackground>
      <Content gap={24}>
        <LayoutContainer>
          <HeroContent className={cn(`tier-${displayTier}`)}>
            <TextContent gap={16}>
              <Group
                direction="horizontal"
                gap={12}
                verticalAlign="center"
                horizontalAlign="start"
              >
                <LoyaltyProgramNameLogo width={104} />
                <LoyaltyTierPill kind="primary" tier={tier}>Double status credits</LoyaltyTierPill>
              </Group>
              <VerticalSpacer gap={12}>
                <Heading variant="heading2">
                  {HeaderText}
                </Heading>
                <BodyText variant="large">
                  {SubtitleText}
                </BodyText>
                <Group direction="horizontal" verticalAlign="center" gap={24} wrap="wrap">
                  <CTAButtonWrapper className={displayTier === 'platinum' ? getThemeClassName({ mobile: 'inverse' }) : undefined}>
                    <TextButton kind="primary" to={`/search?${anywhereSearchQuery.toString()}`}>Browse offers</TextButton>
                  </CTAButtonWrapper>
                  {hasLoyaltyAccount && <TextLink size="medium" to="/societe#benefits">View your progress</TextLink>}
                  {!hasLoyaltyAccount && <TextLink size="medium" onClick={onJoin}>Join now - it's free</TextLink>}
                </Group>
              </VerticalSpacer>
            </TextContent>
            <DoublePointTakeoverTopBenefits />
          </HeroContent>
        </LayoutContainer>
      </Content>
    </HeroContainer>
  )
}

export default React.memo(DoublePointTakeoverHeroSection)
